import type { Type } from '@angular/core';
import type { Routes } from '@angular/router';

/**
 * Satu item menu halaman detail Evaluasi Vendor = satu route anak
 * `/admin/vendor_evaluations/:id/<slug>`.
 */
export interface VendorEvaluationSection {
  /**
   * Segmen URL. Underscore (bukan kebab-case) mengikuti konvensi path area vendor-management yang
   * sudah ada (`vendor_evaluations`, `person_in_charges`).
   *
   * Menu ini TIDAK ada di navigation_tree BE — halaman detail dicapai dari link kolom Name — jadi
   * slug-nya milik kita, bukan `navigable.path`. Yang mengikat cuma `overview`: itu tujuan
   * redirect dan tujuan link kolom Name di `vendor-evaluation.config.ts`.
   */
  readonly slug: string;
  /**
   * Key i18n LITERAL. Nilainya WAJIB ada di `public/i18n/en.json` DAN `id.json` — key yang hanya
   * ada di satu bahasa dirender sebagai key mentah, tanpa error.
   *
   * Ini bukan "key dinamis" yang dilarang: key-nya tidak disusun dari nilai (`'section.' + slug`),
   * melainkan string literal yang seluruhnya terlihat di berkas ini.
   */
  readonly labelKey: string;
  /**
   * Komponen isi kanan. `undefined` → memakai `VendorEvaluationSectionPlaceholderComponent`.
   *
   * Saat sebuah section akhirnya dikerjakan, isi field ini — tidak ada berkas lain yang perlu
   * disentuh, route-nya ikut berubah dengan sendirinya.
   */
  readonly load?: () => Promise<Type<unknown>>;
  /**
   * `true` → item menu DISABLED selama state evaluasi (`GET evaluation/evaluations/:id`) masih
   * `DRAFT`. Dibaca shell lewat `VendorEvaluationDetailStore.isDraft`, satu-satunya tempat aturan
   * DRAFT ditulis.
   */
  readonly disabledWhenDraft?: boolean;
  /**
   * Route anak yang **TIDAK muncul di menu** — halaman yang hanya dicapai dari link di dalam isi
   * section (mis. tanggal Konsep/Terkirim & label "Riwayat" di tabel Formulir).
   *
   * Ditulis di sini, bukan langsung di berkas route, karena daftarnya dipakai DUA KALI seperti
   * `slug`: membangun route anak shell, DAN menentukan menu mana yang tetap tersorot saat halaman
   * itu terbuka (`ownedSlugs()` di `vendor-evaluation-detail.ts`). Dua daftar terpisah akan
   * lama-lama berbeda dan muncul sebagai menu yang padam di halaman anak — bukan sebagai error.
   *
   * `path` boleh bersegmen banyak dan berparameter; **segmen pertamanya** yang menentukan
   * kepemilikan menu. Karena itu `form_history/:vendorId` pun dimiliki item `forms`.
   */
  readonly children?: readonly VendorEvaluationSectionChild[];
}

export interface VendorEvaluationSectionChild {
  /** Relatif terhadap route shell, mis. `'forms/:formId'`. */
  readonly path: string;
  /** Key i18n LITERAL — aturannya sama dengan `labelKey` di atas. */
  readonly labelKey: string;
  /** Pasangan `VendorEvaluationSection.load`. `undefined` → placeholder "Segera hadir". */
  readonly load?: () => Promise<Type<unknown>>;
}

export interface VendorEvaluationSectionGroup {
  readonly titleKey: string;
  readonly items: readonly VendorEvaluationSection[];
}

/**
 * Menu halaman detail Evaluasi Vendor — **DIPAKAI DUA KALI**: membangun `children` route
 * (`vendor-management.routes.ts`) dan merender sidebar (`vendor-evaluation-detail.html`).
 *
 * Jangan menduplikasi daftarnya di template. Dua daftar yang lama-lama berbeda muncul sebagai
 * menu yang menunjuk 404 — bukan sebagai error build.
 *
 * Urutan array = urutan tampil. Ketiga section dan kedua halaman anaknya (detail formulir, riwayat
 * formulir) sudah berisi; tidak ada lagi entry yang memakai placeholder "Segera hadir".
 */
export const VENDOR_EVALUATION_SECTION_GROUPS: readonly VendorEvaluationSectionGroup[] = [
  {
    titleKey: 'vendorEvaluation.detail.group.info',
    items: [
      {
        slug: 'overview',
        labelKey: 'vendorEvaluation.detail.section.overview',
        load: () =>
          import('../pages/vendor-evaluation-detail/sections/overview/vendor-evaluation-overview').then(
            (c) => c.VendorEvaluationOverviewComponent,
          ),
      },
      {
        slug: 'assignments',
        labelKey: 'vendorEvaluation.detail.section.assignment',
        load: () =>
          import('../pages/vendor-evaluation-detail/sections/assignments/vendor-evaluation-assignments').then(
            (c) => c.VendorEvaluationAssignmentsComponent,
          ),
      },
      {
        slug: 'forms',
        labelKey: 'vendorEvaluation.detail.section.form',
        disabledWhenDraft: true,
        load: () =>
          import('../pages/vendor-evaluation-detail/sections/forms/vendor-evaluation-forms').then(
            (c) => c.VendorEvaluationFormsComponent,
          ),
        // Keduanya dicapai dari sel tabel section ini (dan `form_history` juga dari footer
        // halaman detail formulir).
        children: [
          {
            path: 'forms/:formId',
            labelKey: 'vendorEvaluation.formDetail.title',
            load: () =>
              import('../pages/vendor-evaluation-detail/sections/form-detail/vendor-evaluation-form-detail').then(
                (c) => c.VendorEvaluationFormDetailComponent,
              ),
          },
          {
            path: 'form_history/:vendorId',
            labelKey: 'vendorEvaluation.formHistory.title',
            load: () =>
              import('../pages/vendor-evaluation-detail/sections/form-history/vendor-evaluation-form-history').then(
                (c) => c.VendorEvaluationFormHistoryComponent,
              ),
          },
        ],
      },
    ],
  },
];

/** Rata — untuk membangun `children` route. Turunan dari grup, jangan ditulis ulang. */
export const VENDOR_EVALUATION_SECTIONS: readonly VendorEvaluationSection[] =
  VENDOR_EVALUATION_SECTION_GROUPS.flatMap((group) => group.items);

/**
 * Slug section pertama = tujuan redirect `/admin/vendor_evaluations/:id` dan tujuan link kolom
 * Name di tabel list. DITURUNKAN dari daftar, bukan ditulis ulang sebagai literal di dua tempat.
 */
export const VENDOR_EVALUATION_DEFAULT_SECTION = VENDOR_EVALUATION_SECTIONS[0].slug;

/**
 * Isi sementara "Segera hadir" — satu komponen untuk semua route yang belum dikerjakan; judulnya
 * datang dari `data.labelKey`, jadi tidak perlu berkas komponen per halaman.
 */
const loadPlaceholder = () =>
  import('../pages/vendor-evaluation-detail/sections/section-placeholder').then(
    (c) => c.VendorEvaluationSectionPlaceholderComponent,
  );

/**
 * `children` route shell halaman detail.
 *
 * Ditaruh di sini, bukan dirakit inline di berkas route: "daftar section" dan "perakit route dari
 * daftar" harus tinggal serumah.
 *
 * `data.labelKey` diteruskan supaya isi kanan tahu judul section-nya tanpa mencocokkan URL
 * sendiri. `title` memakai key i18n (`I18nTitleStrategy`), bukan string mentah.
 */
export function buildVendorEvaluationDetailChildren(): Routes {
  return [
    // URL tanpa slug → section pertama. `pathMatch: 'full'` WAJIB: tanpa itu path kosong cocok
    // dengan AWALAN URL mana pun di bawah shell ini, sehingga `/…/:id/assignments` pun ikut
    // dialihkan ke `overview` dan dua section sisanya tak pernah bisa dibuka.
    {
      path: '',
      redirectTo: VENDOR_EVALUATION_DEFAULT_SECTION,
      pathMatch: 'full',
    },
    ...VENDOR_EVALUATION_SECTIONS.map((section) => ({
      path: section.slug,
      loadComponent: section.load ?? loadPlaceholder,
      title: section.labelKey,
      data: { labelKey: section.labelKey },
    })),
    // Anak non-menu, SESUDAH entry section. Tidak ada risiko saling menelan: `forms` dan
    // `forms/:formId` beda jumlah segmen, dan router mencocokkan seluruh segmen, bukan prefix.
    ...VENDOR_EVALUATION_SECTIONS.flatMap((section) =>
      (section.children ?? []).map((child) => ({
        path: child.path,
        loadComponent: child.load ?? loadPlaceholder,
        title: child.labelKey,
        data: { labelKey: child.labelKey },
      })),
    ),
  ];
}
